import type {
  Commitment,
  CommitmentState,
  CoverEdit,
  SecondaryDocumentRef,
} from '@festina/life-general-types/life'
import {
  assertNotEmpty,
  notEmpty,
  type Optional,
  type ValidatedKey,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Select, TextInput } from '@festina-life/ui/forms'
import { useField } from 'formik'
import { useMemo, useState } from 'react'

import { useComponent } from '../../lenteHooks'

type Props = {
  fieldNamePrefix: ValidatedKey
  commitment: Commitment
  commitmentState: CommitmentState
}

// @beta None of the autogenerated fields are translated, so the ones here aren't either.
export const CoverEdits = ({
  fieldNamePrefix,
  commitment,
  commitmentState,
}: Props) => {
  const CoverEditComponent = useComponent(
    'pages.commitmentEditor.CoverEditComponent',
  )
  assertNotEmpty(CoverEditComponent)

  const [{ value }, , { setValue }] =
    useField<Optional<CoverEdit>>(fieldNamePrefix)

  const [editType, setEditType] = useState<'create' | 'update' | 'delete'>(
    'update',
  )
  const [selectedKey, setSelectedKey] = useState({
    policyKey: '',
    coverKey: '',
  })

  const policyKeys = useMemo(
    () =>
      commitmentState?.policyStates
        ?.map((policy) => policy?.policyKey)
        .filter(notEmpty) ?? [],
    [commitmentState?.policyStates],
  )

  const policyAndCoverKeys = useMemo(
    () =>
      commitmentState?.policyStates
        ?.flatMap((policy) =>
          policy?.coverStates?.filter(notEmpty).map((cover) => ({
            policyKey: policy.policyKey,
            coverKey: cover?.coverKey,
          })),
        )
        .filter(notEmpty) ?? [],
    [commitmentState?.policyStates],
  )

  const getInitialState = (): CoverEdit => {
    const { policyKey, coverKey } = selectedKey ?? {}

    switch (editType) {
      case 'create':
        return {
          _type: 'CoverEdit',
          editType,
          policyKey,
          cover: {
            _type: 'Cover',
            templateSourceRef: {
              _type: 'CoverTemplate',
              secondaryId: coverKey,
            } as SecondaryDocumentRef,
          },
        }
      case 'update':
        return {
          _type: 'CoverEdit',
          editType,
          policyKey,
          coverState: commitmentState?.policyStates
            ?.find((policy) => policy?.policyKey === policyKey)
            ?.coverStates?.find((cover) => cover?.coverKey === coverKey),
          cover: commitment?.policies
            ?.find((policy) => policy?.policyKey === policyKey)
            ?.covers?.find((cover) => cover?.coverKey === coverKey),
        }
      case 'delete':
        return {
          _type: 'CoverEdit',
          editType,
          policyKey,
          coverKey,
        }
    }
  }

  const isSelectionConfirmed = notEmpty(value) && Object.keys(value).length > 0

  return (
    <>
      <Select
        wrapperProps={{
          // See beta-comment above; lack of translation is intentional
          label: 'Edit type',
        }}
        onChange={(editType) => setEditType(editType!)}
        value={editType}
        options={['create', 'update', 'delete'] as const}
        sort="automatic"
        disabled={isSelectionConfirmed}
      />
      {editType === 'create' ? (
        <>
          <Select
            sort="automatic"
            wrapperProps={{
              // See beta-comment above; lack of translation is intentional
              label: 'Policy key',
            }}
            onChange={(policyKey) =>
              setSelectedKey({
                policyKey: policyKey ?? '',
                coverKey: selectedKey.coverKey,
              })
            }
            value={selectedKey.policyKey}
            options={policyKeys}
            disabled={isSelectionConfirmed}
            isValueCreator
            valueFromString={(value) => value}
          />
          <TextInput
            wrapperProps={{
              // See beta-comment above; lack of translation is intentional
              label: 'Cover template id',
            }}
            onChange={(e) =>
              setSelectedKey({
                policyKey: selectedKey.policyKey,
                coverKey: e.target.value,
              })
            }
            value={selectedKey.coverKey}
            disabled={isSelectionConfirmed}
          />
        </>
      ) : (
        <Select
          wrapperProps={{
            // See beta-comment above; lack of translation is intentional
            label: 'Cover key',
          }}
          onChange={(newValue) =>
            setSelectedKey({
              policyKey: newValue?.policyKey ?? '',
              coverKey: newValue?.coverKey ?? '',
            })
          }
          value={selectedKey}
          options={policyAndCoverKeys}
          sort="automatic"
          disabled={isSelectionConfirmed}
          renderFn={(key) =>
            key.policyKey && key.coverKey
              ? `${key.policyKey}, ${key.coverKey}`
              : ''
          }
        />
      )}
      {isSelectionConfirmed ? (
        <CoverEditComponent fieldNamePrefix={fieldNamePrefix} />
      ) : (
        <Button
          onClick={() => setValue(getInitialState())}
          // See beta-comment above; lack of translation is intentional
        >
          Confirm
        </Button>
      )}
    </>
  )
}
